import { liquidGlassSurfaceFillSx, translateTemplateText } from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { Caption } from '@telegram-apps/telegram-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { getColor } from '../../helpers/color';
import { CategorySummary } from '../../helpers/quest';
import { CategoryIcon } from '../common/CategoryIcon';

export type QuestSummaryProps = {
  totalXP: number;
  categorySummaries: CategorySummary[];
};

const formatTimeSpent = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  if (minutes < 60) {
    return remainingSeconds > 0 ? `${minutes}m ${remainingSeconds}s` : `${minutes}m`;
  }
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
};

export const QuestSummary: React.FC<QuestSummaryProps> = ({ totalXP, categorySummaries }) => {
  const { t } = useTranslation('common');

  return (
    <Box
      sx={{
        width: 'calc(100% - 32px)',
        maxWidth: '100%',
        maxHeight: '100%',
        margin: '0 auto',
        boxSizing: 'border-box',
      }}
    >
      <Stack direction="column" padding="20px 0" gap="15px" sx={{ overflow: 'hidden' }}>
      <Stack direction="column" alignItems="center" gap="10px" sx={{ flexShrink: 0 }}>
        <Caption style={{ fontSize: '32px', fontWeight: 'bold', color: 'var(--tg-theme-text-color)' }}>
          {totalXP} XP
        </Caption>
        <Caption style={{ fontSize: '14px', color: 'var(--tgui--hint_color)' }}>
          {t('quest.summary')}
        </Caption>
      </Stack>
      
      <Stack 
        direction="column" 
        gap="10px" 
        sx={{ 
          overflowY: 'auto',
          flex: 1,
          minHeight: 0,
          paddingBottom: '80px'
        }}
      >
        {categorySummaries.map((summary) => (
          <Stack 
            key={summary.categoryId}
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
            padding="12px 16px"
            sx={{
              boxSizing: 'border-box',
              borderRadius: '12px',
              ...liquidGlassSurfaceFillSx,
            }}
          >
            <Stack direction="row" alignItems="center" gap="12px" flex={1}>
              <Box sx={{
                width: 32,
                height: 32,
                borderRadius: 1.5,
                background: getColor(summary.categoryColor),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <CategoryIcon
                  icon={summary.categoryIcon}
                  iconColor={summary.categoryColor}
                  customIcon={summary.categoryCustomIcon}
                  type="filled"
                  size={18}
                  color="var(--tg-theme-text-color)"
                />
              </Box>
              <Stack direction="column" flex={1} gap={1}>
                <Caption style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--tg-theme-text-color)' }}>
                  {translateTemplateText(t, summary.categoryTemplateId, 'name', summary.categoryName)}
                </Caption>
                <Caption style={{ fontSize: '13px', color: 'var(--tgui--hint_color)' }}>
                  {t('quest.count', { count: summary.questCount })}
                  {summary.totalTimeSpent > 0 && ` • ${t('quest.timeSpent', { time: formatTimeSpent(summary.totalTimeSpent) })}`}
                </Caption>
              </Stack>
            </Stack>
            <Caption style={{ fontSize: '16px', fontWeight: 'bold', color: 'var(--tgui--button_color)' }}>
              +{summary.totalXP} XP
            </Caption>
          </Stack>
        ))}
      </Stack>
      </Stack>
    </Box>
  );
};
